/**
 * How a rotated screen is laid out.
 *
 * Fire TV always drives HDMI in landscape and ignores `android:screenOrientation`,
 * so a physically rotated TV is served by the app rotating its own rendering: it
 * composes into a logical canvas with width and height swapped, then turns that
 * canvas by `rotationDegrees` to fill the landscape framebuffer.
 *
 * The preview frame is the framebuffer — the picture the stick actually sends
 * down the cable — and the canvas inside it is rotated exactly as the Kotlin
 * root composable will be. On a portrait screen the content therefore appears
 * lying on its side in the frame, which is correct: that is what a TV shows
 * before you turn it on the wall.
 */

import type { FitMode, RotationDegrees } from '@pbtv/shared';

export interface PreviewResolution {
  width: number;
  height: number;
}

/** What a Fire TV Stick reports before a device has ever checked in. */
export const DEFAULT_PREVIEW_RESOLUTION: PreviewResolution = { width: 1920, height: 1080 };

export interface CanvasGeometry {
  /** Logical canvas size the app composes into, in device pixels. */
  canvasWidth: number;
  canvasHeight: number;
  /** Canvas size as a percentage of the framebuffer, before the rotation is applied. */
  widthPercent: number;
  heightPercent: number;
  rotationDegrees: RotationDegrees;
}

export function isQuarterTurn(rotationDegrees: RotationDegrees): boolean {
  return rotationDegrees === 90 || rotationDegrees === 270;
}

export function canvasGeometry(
  resolution: PreviewResolution,
  rotationDegrees: RotationDegrees,
): CanvasGeometry {
  const swapped = isQuarterTurn(rotationDegrees);
  const canvasWidth = swapped ? resolution.height : resolution.width;
  const canvasHeight = swapped ? resolution.width : resolution.height;

  // Sized as a share of the framebuffer so the frame can be any size on the page:
  // a quarter-turned canvas is as wide as the frame is tall, and vice versa, so
  // its bounding box after rotation is exactly the frame again.
  return {
    canvasWidth,
    canvasHeight,
    widthPercent: (canvasWidth / resolution.width) * 100,
    heightPercent: (canvasHeight / resolution.height) * 100,
    rotationDegrees,
  };
}

/** The manifest's `fit` in the CSS spelling. `stretch` is CSS's `fill`. */
export function cssObjectFit(fit: FitMode): 'contain' | 'cover' | 'fill' {
  switch (fit) {
    case 'cover':
      return 'cover';
    case 'stretch':
      return 'fill';
    default:
      return 'contain';
  }
}

export function rotationLabel(rotationDegrees: RotationDegrees): string {
  switch (rotationDegrees) {
    case 90:
      return 'Portrait, TV turned clockwise';
    case 180:
      return 'Landscape, upside down';
    case 270:
      return 'Portrait, TV turned anticlockwise';
    default:
      return 'Landscape';
  }
}

/** The three fit modes, in the order the editor offers them. */
export const FIT_MODES: readonly FitMode[] = ['contain', 'cover', 'stretch'];

/**
 * What a fit mode does, said without the word itself. "Contain" means nothing to
 * a store owner deciding whether their poster will have black bars down the
 * side; "whole image, letterboxed" answers the question they are actually
 * asking. The manifest keeps the one-word name, the panel never shows it alone.
 */
export function fitLabel(fit: FitMode): string {
  switch (fit) {
    case 'cover':
      return 'Fill the screen, crop the edges';
    case 'stretch':
      return 'Fill the screen, stretch to fit';
    default:
      return 'Whole image, letterboxed';
  }
}

/** The consequence of that choice, for the row of help text under the control. */
export function fitHint(fit: FitMode): string {
  switch (fit) {
    case 'cover':
      return 'Nothing is left over, so no bars — but whatever falls outside the shape of the screen is cut off.';
    case 'stretch':
      return 'No bars and nothing cropped, at the cost of the proportions: circles come out as ovals.';
    default:
      return 'Every pixel of the file is shown. Whatever is left over is filled with the letterbox colour.';
  }
}
